import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getArticle } from "@/mocks/articles";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticle(slug) : null;

  if (!article) {
    return <NotFound />;
  }

  return (
    <main>
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "image": article.image,
            "datePublished": article.date,
            "dateModified": article.date,
            "author": {
              "@type": "Person",
              "name": article.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "Itaobuy Spreadsheet",
              "url": "https://itaobuy.wtf",
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://itaobuy.wtf/article/" + article.slug
            }
          })
        }}
      />

      {/* Article Header */}
      <section className="relative pt-24 pb-8 md:pt-32 md:pb-12 bg-background-50">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-600 transition-colors mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xs" />
            Back to Itaobuy Sheet Home
          </Link>

          <div className="max-w-3xl">
            <p className="text-xs text-foreground-500 uppercase tracking-widest mb-3">{article.date} · {article.author}</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-6 md:pb-10">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <div className="rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-auto max-h-[400px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Article Body */}
      <section className="py-6 md:py-10">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Main Content */}
            <div className="lg:w-[65%]">
              <article
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Bottom CTA */}
              <div className="mt-12 p-8 rounded-lg bg-background-100 border border-background-200/60 text-center">
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">Ready to Start Exploring?</h3>
                <p className="text-sm text-foreground-600 mb-5">
                  Browse all 14 categories in the itaobuy spreadsheet and find your next favorite product.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Back to Home
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-5 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Buy It Now
                    <i className="ri-arrow-right-up-line ml-1 text-xs" />
                  </a>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:w-[35%]">
              <div className="sticky top-24 space-y-6">
                <div className="p-5 rounded-lg bg-background-100 border border-background-200/60">
                  <h4 className="text-sm font-semibold text-foreground-900 mb-2">About this Guide</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed mb-3">
                    Written by the Itaobuy Editorial Team. We independently research and verify every product category in the itaobuy spreadsheet to help international shoppers find the best China-sourced products safely.
                  </p>
                  <p className="text-xs text-foreground-500">Published: {article.date}</p>
                </div>

                <div className="p-5 rounded-lg bg-background-100 border border-background-200/60">
                  <h4 className="text-sm font-semibold text-foreground-900 mb-3">Quick Links</h4>
                  <div className="space-y-2">
                    <Link to="/" className="block text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
                      Itaobuy Sheet Home
                    </Link>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="block text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      Visit Main Platform
                      <i className="ri-external-link-line ml-1 text-xs" />
                    </a>
                  </div>
                </div>

                {/* Sidebar Image */}
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20workspace%20with%20product%20catalog%20open%20next%20to%20fresh%20flowers%20in%20vase%20on%20cream%20desk%20warm%20soft%20daylight%20clean%20editorial%20photography%20sophisticated%20atmosphere&width=800&height=1000&seq=article-sidebar-generic-v3&orientation=portrait"
                    alt="Itaobuy spreadsheet workspace"
                    title="Itaobuy Spreadsheet — Product Sourcing Workspace"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .prose-custom h2 {
          font-family: var(--font-heading);
          font-size: 1.5rem;
          font-weight: 600;
          color: oklch(var(--foreground-950));
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          line-height: 1.3;
        }
        .prose-custom h3 {
          font-family: var(--font-heading);
          font-size: 1.2rem;
          font-weight: 600;
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .prose-custom p {
          font-size: 0.9375rem;
          line-height: 1.8;
          color: oklch(var(--foreground-700));
          margin-bottom: 1.25rem;
        }
        .prose-custom strong {
          color: oklch(var(--foreground-950));
          font-weight: 600;
        }
        .prose-custom ul, .prose-custom ol {
          margin-bottom: 1.25rem;
          padding-left: 1.5rem;
        }
        .prose-custom li {
          font-size: 0.9375rem;
          line-height: 1.8;
          color: oklch(var(--foreground-700));
          margin-bottom: 0.5rem;
        }
        .prose-custom a {
          color: oklch(var(--primary-600));
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .prose-custom a:hover {
          color: oklch(var(--primary-700));
        }
      `}</style>
    </main>
  );
}